import {
  InputAndSubtitle,
  SubtitleRowStyled,
  SubtitleTextStyled,
  TextInputProps,
  TextValue,
} from 'components/form/inputs/InputStyles'
// eslint-disable-next-line boundaries/dependencies
import { useGeckoLocale } from 'features/locale'
import findIndex from 'lodash/findIndex'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import isNumber from 'lodash/isNumber'
import {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import styled from 'styled-components'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'
import { parseCoordinatesFromString } from 'types/assetModel/features/geckoPolygonalPlate'
import { assetModelPrecision } from 'utils/constants'
import { useKeybindingControl } from 'utils/useKeybindingControl'
import { v4 as uuid4 } from 'uuid'

import { AddRowButton } from './helpers/AddRowButton'
import {
  Coordinate,
  EditableCoordinate,
  isFullyDefinedCoordinate,
} from './helpers/coordinateTranslations'
import { DeleteButton } from './helpers/DeleteButton'
import { PointAndClickButton } from './helpers/PointAndClickButton'

enum Axis {
  X = 'x',
  Y = 'y',
}

export type CoordinateInputTablePointAndClickApi =
  | {
      isInPointSelectionMode: false
      enterPointSelectionMode: null | (() => void)
    }
  | {
      isInPointSelectionMode: true
      exitPointSelectionMode: () => void
      selectedPoint: { x: GeckoMeasurement; y: GeckoMeasurement } | undefined
      clearSelection: () => void
    }

export type CoordinatesInputProps = {
  coordinates: ReadonlyArray<Coordinate>
  pointAndClickApi: CoordinateInputTablePointAndClickApi
  $subtitleText?: string
  $subtitleTextColor?: string
  isReadonly: boolean
  disabled?: boolean
  onChange?: (
    coords: ReadonlyArray<Omit<EditableCoordinate, 'stableKey'>>
  ) => void
  highlightCoordinate: (coordinate: Coordinate | null) => void
}
export const CoordinatesInputTable: FC<CoordinatesInputProps> = ({
  coordinates,
  $subtitleText,
  $subtitleTextColor,
  isReadonly,
  disabled,
  onChange,
  pointAndClickApi,
  highlightCoordinate,
}) => {
  const { getPreferredMeasurementUnit } = useGeckoLocale()

  const firstCoordinate = coordinates.at(0)
  const units = useMemo(
    () => ({
      [Axis.X]:
        firstCoordinate?.x.unit ??
        getPreferredMeasurementUnit(GeckoMeasurementUnit.Meters),
      [Axis.Y]:
        firstCoordinate?.y.unit ??
        getPreferredMeasurementUnit(GeckoMeasurementUnit.Meters),
    }),
    [
      firstCoordinate?.y.unit,
      firstCoordinate?.x.unit,
      getPreferredMeasurementUnit,
    ]
  )

  const [localCoords, setLocalCoords] = useState<
    ReadonlyArray<EditableCoordinate>
  >(() => coordinates.map((c) => generateEditableCoordFromStableCoord(c)))

  const isEditable = !isReadonly && !disabled

  /**
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   *            Highlight Coordinate Logic
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   */

  const highlightedKeyRef = useRef<string | undefined>(undefined)

  const highlightCoordinateTunnel = useRef(highlightCoordinate)
  highlightCoordinateTunnel.current = highlightCoordinate

  const highlightCoordByKey = useCallback(
    (key: string | undefined) => {
      if (key === highlightedKeyRef.current) return
      if (!key) {
        highlightCoordinateTunnel.current(null)
        highlightedKeyRef.current = undefined
        return
      }
      const coord = localCoords.find(({ stableKey }) => stableKey === key)
      if (coord && isFullyDefinedCoordinate(coord)) {
        highlightCoordinateTunnel.current(coord)
        highlightedKeyRef.current = key
      } else {
        highlightCoordinateTunnel.current(null)
        highlightedKeyRef.current = undefined
      }
    },
    [localCoords]
  )

  /**
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   *            Coordinate Updater Functions
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   */

  const onChangeTunnel = useRef(onChange)
  onChangeTunnel.current = onChange
  /**
   * A function to be used for updating coordinates.  This function handles both updating internal state and calling the broader form's `onChange`
   * @param updator - a function which will be called with the most up-to-date version of the coordinates.
   *                  The return will be used as the "to update" value.
   */
  const updateLocalCoords = useCallback(
    (
      updator: (
        prev: ReadonlyArray<EditableCoordinate>
      ) => ReadonlyArray<EditableCoordinate>
    ) => {
      setLocalCoords((prev) => {
        const updatedCoords = updator(prev)
        onChangeTunnel.current?.(updatedCoords)
        return updatedCoords
      })
    },
    [setLocalCoords]
  )

  /**
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   *                      Input Handler Functions
   *  These are the functions to use in the coordinates table.
   *  Each should use `updateLocalCoords` to update coordinate values.
   */

  function handleCellChange(
    coordinateKey: string,
    axis: Axis,
    rawValue: string
  ) {
    const parsedNum = parseFloat(rawValue)
    const numValue = isNaN(parsedNum) ? undefined : parsedNum
    updateLocalCoords((prev) =>
      prev.map((curCoord) =>
        curCoord.stableKey === coordinateKey
          ? {
              ...curCoord,
              [axis]: !isNil(numValue)
                ? GeckoMeasurement.CreateFromValue(numValue, units[axis])
                : undefined,
            }
          : curCoord
      )
    )
  }

  function handleAddCoordinate(atIndex: number) {
    const newKey = uuid4()
    updateLocalCoords((prev) => [
      ...prev.slice(0, atIndex),
      {
        x: undefined,
        y: undefined,
        stableKey: newKey,
      },
      ...prev.slice(atIndex),
    ])
    return newKey
  }

  function deleteCoordinateWithId(keyToDelete: string) {
    if (highlightedKeyRef.current === keyToDelete) {
      highlightCoordByKey(undefined)
    }
    updateLocalCoords((prev) => {
      const indexToRemove = prev.findIndex(
        ({ stableKey }) => stableKey === keyToDelete
      )
      return [...prev.slice(0, indexToRemove), ...prev.slice(indexToRemove + 1)]
    })
  }

  /**
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   *                         Point-and-Click Handlers
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   */

  const canEnterPointAndClickMode = pointAndClickApi.isInPointSelectionMode
    ? true
    : !isNil(pointAndClickApi.enterPointSelectionMode)

  const [curPointAndClickCoordKey, setCurPointAndClickCoordKey] = useState<
    string | undefined
  >(undefined)

  function toggleCoordinateForPointAndClick(coordinateKey: string) {
    setCurPointAndClickCoordKey((prev) => {
      if (prev === coordinateKey) {
        if (pointAndClickApi.isInPointSelectionMode) {
          pointAndClickApi.exitPointSelectionMode()
        }
        return undefined
      }
      if (!pointAndClickApi.isInPointSelectionMode) {
        pointAndClickApi.enterPointSelectionMode?.()
      }
      return coordinateKey
    })
  }

  // Listen to the point-and-click api and update the coordinates in the table when
  // the value changes
  useEffect(() => {
    if (
      pointAndClickApi.isInPointSelectionMode &&
      !isNil(pointAndClickApi.selectedPoint) &&
      !isNil(curPointAndClickCoordKey)
    ) {
      updateLocalCoords((prev) =>
        prev.map((curCoord) =>
          curCoord.stableKey === curPointAndClickCoordKey
            ? {
                stableKey: curCoord.stableKey,
                x: pointAndClickApi.selectedPoint?.x
                  .convertTo(GeckoMeasurementUnit.Meters)
                  .toPrecision(assetModelPrecision)
                  .convertTo(units[Axis.X]),

                y: pointAndClickApi.selectedPoint?.y
                  .convertTo(GeckoMeasurementUnit.Meters)
                  .toPrecision(assetModelPrecision)
                  .convertTo(units[Axis.Y]),
              }
            : curCoord
        )
      )
      pointAndClickApi.clearSelection()
    }
  }, [curPointAndClickCoordKey, pointAndClickApi, units, updateLocalCoords])

  useEffect(() => {
    highlightCoordByKey(curPointAndClickCoordKey)
  }, [curPointAndClickCoordKey, highlightCoordByKey])

  // Handle exiting point-and-click mode when this component is unmounted
  const exitPointAndClickPipe = useRef<() => void>(
    pointAndClickApi.isInPointSelectionMode
      ? pointAndClickApi.exitPointSelectionMode
      : () => {}
  )
  exitPointAndClickPipe.current = pointAndClickApi.isInPointSelectionMode
    ? pointAndClickApi.exitPointSelectionMode
    : () => {}
  useEffect(() => {
    return () => {
      exitPointAndClickPipe.current()
      highlightCoordinateTunnel.current(null)
    }
  }, [])

  useKeybindingControl((e) => {
    if (!pointAndClickApi.isInPointSelectionMode) {
      return
    }

    setCurPointAndClickCoordKey((prev) => {
      if (!prev || isEmpty(localCoords)) return prev
      const currentCoordIndex = findIndex(
        localCoords,
        ({ stableKey }) => stableKey === prev
      )
      const addCoordBefore = () => handleAddCoordinate(currentCoordIndex)
      const addCoordAfter = () => handleAddCoordinate(currentCoordIndex + 1)

      switch (e.key) {
        case 'w': {
          if (currentCoordIndex === 0) {
            return addCoordBefore()
          }
          return localCoords[currentCoordIndex - 1].stableKey
        }
        case 's': {
          if (currentCoordIndex + 1 === localCoords.length) {
            return addCoordAfter()
          }
          return localCoords[currentCoordIndex + 1].stableKey
        }
        // holding shift
        case 'W': {
          return addCoordBefore()
        }
        case 'S': {
          return addCoordAfter()
        }
      }
      return prev
    })
  })

  /**
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   *                         UI-Return
   * - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
   */

  return (
    <InputAndSubtitle>
      <CoordinatesTable>
        <thead>
          <CoordinatesHeaderRow>
            {isEditable && canEnterPointAndClickMode && (
              <CoordinatesHeaderCell />
            )}
            <CoordinatesHeaderCell>#</CoordinatesHeaderCell>

            <CoordinatesHeaderCell>
              X{units[Axis.X] && ` (${units[Axis.X].name})`}
            </CoordinatesHeaderCell>
            <CoordinatesHeaderCell>
              Y{units[Axis.Y] && ` (${units[Axis.Y].name})`}
            </CoordinatesHeaderCell>
            {isEditable && <CoordinatesHeaderCell />}
          </CoordinatesHeaderRow>
        </thead>
        <tbody>
          {localCoords.map(({ stableKey, ...coord }, index) => (
            <Fragment key={stableKey}>
              <CoordinatesRow
                $hasBottomBorder={isReadonly}
                onFocus={(e) => {
                  if (e.target instanceof HTMLInputElement) {
                    highlightCoordByKey(stableKey)
                  }
                }}
                onBlur={(e) => {
                  if (
                    !e.currentTarget.contains(e.relatedTarget) &&
                    curPointAndClickCoordKey !== stableKey
                  ) {
                    highlightCoordByKey(undefined)
                  }
                }}>
                <PointAndClickCell></PointAndClickCell>
                {isEditable && canEnterPointAndClickMode && (
                  <PointAndClickCell>
                    <PointAndClickButton
                      onClick={() =>
                        toggleCoordinateForPointAndClick(stableKey)
                      }
                      isActive={curPointAndClickCoordKey === stableKey}
                    />
                  </PointAndClickCell>
                )}
                <RowIndexCell>{index + 1}</RowIndexCell>

                <CoordinateReadOrWriteDisplay
                  value={coord.x?.num}
                  isReadonly={isReadonly}
                  disabled={disabled}
                  onChange={(raw) => handleCellChange(stableKey, Axis.X, raw)}
                />
                <CoordinateReadOrWriteDisplay
                  value={coord.y?.num}
                  isReadonly={isReadonly}
                  disabled={disabled}
                  onChange={(raw) => handleCellChange(stableKey, Axis.Y, raw)}
                />
                {isEditable && (
                  <DeleteCell>
                    <DeleteButton
                      onClick={() => deleteCoordinateWithId(stableKey)}
                    />
                  </DeleteCell>
                )}
              </CoordinatesRow>
              {isEditable && (
                <InsertCoordinateRow>
                  <InsertCoordinateCell>
                    <AddRowButton
                      onClick={() => {
                        const newStableKey = handleAddCoordinate(index + 1)
                        toggleCoordinateForPointAndClick(newStableKey)
                      }}
                    />
                  </InsertCoordinateCell>
                </InsertCoordinateRow>
              )}
            </Fragment>
          ))}
          {isEditable && localCoords.length === 0 && (
            <InsertCoordinateRow>
              <InsertCoordinateCell>
                <AddRowButton
                  onClick={() => {
                    const newStableKey = handleAddCoordinate(0)
                    toggleCoordinateForPointAndClick(newStableKey)
                  }}
                />
              </InsertCoordinateCell>
            </InsertCoordinateRow>
          )}
        </tbody>
      </CoordinatesTable>

      {!isReadonly && $subtitleText && (
        <SubtitleRowStyled>
          <SubtitleTextStyled $subtitleTextColor={$subtitleTextColor}>
            {$subtitleText}
          </SubtitleTextStyled>
        </SubtitleRowStyled>
      )}
    </InputAndSubtitle>
  )
}

const CoordinateReadOrWriteDisplay: FC<{
  value: number | undefined
  isReadonly: boolean
  disabled?: boolean
  onChange: (rawValue: string) => void
}> = ({ value, isReadonly, disabled, onChange }) => (
  <CoordinatesCell>
    {isReadonly ? (
      <TextValue>{value}</TextValue>
    ) : (
      <CoordinateTextInput
        onChange={(e) => onChange(e.target.value)}
        value={value}
        disabled={disabled}
        $isValid={!isNil(value)}
      />
    )}
  </CoordinatesCell>
)

/**
 *  ------------------------------------------------------------------------------------------------------------------
 *                                                Styled Components
 *  ------------------------------------------------------------------------------------------------------------------
 */

export const CoordinatesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${({ theme }) => theme.fontFamily.manrope};
`

export const CoordinatesHeaderRow = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.forecast.border};
`

export const CoordinatesHeaderCell = styled.th`
  padding: 0 0.5rem;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-align: left;
`

export const CoordinatesRow = styled.tr<{ $hasBottomBorder: boolean }>`
  border-bottom: ${({ $hasBottomBorder, theme }) =>
    $hasBottomBorder ? `1px solid ${theme.forecast.border}` : 'none'};

  &:last-child {
    border-bottom: none;
  }
`

export const CoordinatesCell = styled.td`
  box-sizing: border-box;
`

export const CoordinateTextInput = styled.input<{ $isValid: boolean }>`
  width: 100%;
  font-family: ${({ theme }) => theme.fontFamily.manrope};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
  background-color: ${({ theme }) => theme.forecast.cardGroupBackground};
  border: 1px solid
    ${({ $isValid, theme }) =>
      $isValid ? theme.forecast.border2 : theme.forecast.danger};
  border-radius: 0.25rem;
  box-shadow: none;
  outline: none;

  &:hover {
    cursor: pointer;
  }

  &:focus {
    cursor: initial;
    border-color: ${({ theme, $isValid }) =>
      $isValid ? theme.forecast.secondaryText : theme.forecast.warning};
  }
`

export const RowIndexCell = styled.td`
  font-size: 14px;
  padding: 0.25rem;
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.tertiaryText};
  width: 2rem;
  height: 2rem;
  text-align: center;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`

export const PointAndClickCell = styled.td`
  height: 2rem;
  width: max-content;
`

const InsertCoordinateRow = styled.tr`
  cursor: pointer;
  width: 100%;
`

const InsertCoordinateCell = styled.td.attrs({
  colSpan: 10,
})`
  width: 100%;
  column-span: all;
`

const DeleteCell = styled.td`
  width: 2rem;
  text-align: center;
`

/**
 * ------------------------------------------------------------------------------------------------------------------
 *                                                Helper Fns
 * ------------------------------------------------------------------------------------------------------------------
 */

export function convertPossibleFieldValuesToCoordinates(
  inputs: TextInputProps['value'],
  units: GeckoMeasurementUnit
): CoordinatesInputProps['coordinates'] {
  if (isNil(inputs)) return []

  const fieldValueAsString = Array.isArray(inputs)
    ? inputs.join(',')
    : isNumber(inputs)
      ? inputs.toString()
      : (inputs as string)

  const coordinatesAsXYArray = parseCoordinatesFromString(fieldValueAsString)
  return coordinatesAsXYArray.map(({ x: xInput, y: yInput }) => ({
    x: GeckoMeasurement.CreateFromValue(xInput, units),
    y: GeckoMeasurement.CreateFromValue(yInput, units),
  }))
}

function generateEditableCoordFromStableCoord(
  coord: Coordinate
): EditableCoordinate {
  return {
    x: !isNaN(coord.x.num) ? coord.x : undefined,
    y: !isNaN(coord.y.num) ? coord.y : undefined,
    stableKey: uuid4(),
  }
}
